import { Base } from '@studiometa/js-toolkit';
import Flickity from 'flickity-fade';

/**
 * Homepage class.
 */
export default class Slider extends Base {
  /**
   * Class cofnig
   * @type {object}
   */
  static config = {
    name: 'Slider',
    refs: ['items[]'],
    options: {
      draggable: { type: Boolean, default: true },
      freeScroll: { type: Boolean, default: false },
      wrapAround: { type: Boolean, default: false },
      autoPlay: { type: Number, default: 0 },
      fade: { type: Boolean, default: false },
      contain: { type: Boolean, default: false },
      adaptiveHeight: { type: Boolean, default: false },
      watchCSS: { type: Boolean, default: false },
      freeScrollFriction: { type: Boolean, default: true },
      lazyLoad: { type: Number, default: 0 },
      cellAlign: { type: String, default: 'center' },
      prevNextButtons: { type: Boolean, default: true },
      pageDots: { type: Boolean, default: false },
      scrollbar: { type: Boolean, default: false },
    },
  };

  /**
   * Check if this slider uses watchCSS (responsive behavior)
   * @returns {boolean}
   */
  hasWatchCSS() {
    return this.$el.classList.contains('watch-css');
  }

  /**
   * Check if slider should be active (mobile only when watch-css class is present)
   * The 'm' breakpoint is 1024px
   * @returns {boolean}
   */
  shouldBeActive() {
    // If watch-css class is present, only activate on mobile (below 1024px)
    if (this.hasWatchCSS()) {
      return window.innerWidth < 1024;
    }
    return true;
  }

  /**
   * Log a nice message when app is ready.
   * @returns {void}
   */
  mounted() {
    if (this.$refs.items) {
      // If watch-css class is present and we're on desktop, don't initialize Flickity at all
      if (this.hasWatchCSS() && !this.shouldBeActive()) {
        // Desktop mode - no slider, just flex grid
        this.$el.dispatchEvent(new CustomEvent('slider:ready', { bubbles: true }));

        // Set up resize listener to initialize slider if window is resized to mobile
        this.handleResize = () => {
          if (this.shouldBeActive() && !this.slider) {
            this.initSlider();
          } else if (!this.shouldBeActive() && this.slider) {
            this.destroySlider();
          }
        };
        window.addEventListener('resize', this.handleResize);
        return;
      }

      this.initSlider();
    }
  }

  /**
   * Initialize Flickity slider
   */
  initSlider() {
    const params = {
      cellSelector: '[data-ref="items[]"]',
    };

    // Create scrollbar elements
    if (this.$options.scrollbar && !this.scrollbarEl) {
      this.scrollbarEl = document.createElement('div');
      this.scrollbarEl.classList.add('slider__scrollbar');
      this.$el.append(this.scrollbarEl);

      this.barEl = document.createElement('div');
      this.barEl.classList.add('bar');
      this.scrollbarEl.append(this.barEl);

      params.on = {
        scroll: (progress) => {
          if (this.barEl) {
            this.barEl.style.width = `${progress * 100}%`;
          }
        },
      };
    }

    if (this.scrollbarEl) {
      this.scrollbarEl.style.display = '';
    }

    const atts = Object.assign({}, this.$options, params);
    // Don't use watchCSS in Flickity - we handle it ourselves
    atts.watchCSS = false;

    this.slider = new Flickity(this.$el, atts);
    this.setupResizeHandlers();
    this.$el.dispatchEvent(new CustomEvent('slider:ready', { bubbles: true }));
  }

  /**
   * Destroy Flickity slider
   */
  destroySlider() {
    if (this.slider) {
      this.slider.destroy();
      this.slider = null;
    }
    if (this.scrollbarEl) {
      this.scrollbarEl.style.display = 'none';
    }
  }

  /**
   * Cleanup on destroy
   */
  destroyed() {
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
    this.destroySlider();
  }

  /**
   * Setup resize handlers for when slider is active
   */
  setupResizeHandlers() {
    // Resize slider after each image loads
    const images = this.$el.querySelectorAll('img');
    images.forEach((img) => {
      const handleLoad = () => {
        if (this.slider) {
          this.slider.reloadCells();
          this.slider.resize();
        }
      };
      if (img.complete) {
        handleLoad();
      } else {
        img.addEventListener('load', handleLoad);
      }
    });

    // Fallback: resize after delays to ensure proper calculation
    setTimeout(() => {
      if (this.slider) {
        this.slider.reloadCells();
        this.slider.resize();
      }
    }, 100);

    setTimeout(() => {
      if (this.slider) {
        this.slider.reloadCells();
        this.slider.resize();
      }
    }, 500);
  }
}
